import { useEffect, useState } from 'react';
import { API_URL } from '../../config/api';
import { readApiJson } from '../../utils/apiErrors';

// Each status field on a Legal record has its own allowed set of values —
// not every field can be "NA" (Legal/Signatory/Current status must always
// reflect a real state), and only Current Status has "Signed copy awaited".
export const FIELD_STATUS_OPTIONS = {
    legal_status: ['Cancelled', 'Completed', 'In Progress', 'On Hold'],
    finance_status: ['Cancelled', 'Completed', 'In Progress', 'NA', 'On Hold'],
    kosmos_signatory_status: ['Cancelled', 'Completed', 'In Progress', 'On Hold'],
    partner_signatory_status: ['Cancelled', 'Completed', 'In Progress', 'On Hold'],
    current_status: ['Cancelled', 'Completed', 'In Progress', 'On Hold', 'Signed copy awaited'],
    saved_on_relativity: ['Completed', 'In Progress', 'NA'],
};

export const STATUS_FIELD_LABELS = {
    legal_status: 'Legal Status',
    finance_status: 'Finance Status',
    kosmos_signatory_status: '1Kosmos Signatory Status',
    partner_signatory_status: 'Partner Signatory Status',
    current_status: 'Current Status',
    saved_on_relativity: 'Saved on Relativity',
};
export const STATUS_FIELD_ORDER = Object.keys(STATUS_FIELD_LABELS);

export function statusToneClass(value) {
    const v = (value || '').trim().toLowerCase();
    if (v === 'completed') return 'legal-status-completed';
    if (v === 'in progress') return 'legal-status-progress';
    if (v === 'on hold') return 'legal-status-hold';
    if (v === 'pending') return 'legal-status-pending';
    if (v === 'signed copy awaited') return 'legal-status-awaited';
    if (v === 'cancelled') return 'legal-status-cancelled';
    if (v === 'na') return 'legal-status-na';
    return '';
}

export function StatusChip({ value }) {
    if (!value) return <span className="bm-td-muted">—</span>;
    return <span className={`legal-status-chip ${statusToneClass(value)}`}>{value}</span>;
}

export function fmtDate(iso) {
    if (!iso) return '—';
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// Whole days between two "YYYY-MM-DD" strings — used both for the timeline
// popup's per-entry duration and the Aging tab's per-field "current stage"
// bifurcation, so the two screens can never disagree on how a day is counted.
export function daysBetweenIso(fromIso, toIso) {
    const from = new Date(`${fromIso}T00:00:00`);
    const to = new Date(`${toIso}T00:00:00`);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
    return Math.max(0, Math.round((to - from) / 86400000));
}

// ── Timeline trigger — a small icon button any table row can drop in to
// open LegalTimelineModal for that record. ──────────────────────────────────
export function TimelineButton({ onClick, title = 'View status timeline' }) {
    return (
        <button className="legal-action-btn" title={title} onClick={onClick}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14"/></svg>
        </button>
    );
}

function StatusUpdateForm({ field, initial, onCancel, onSubmit }) {
    const [value, setValue] = useState(initial?.status_value || '');
    const [effectiveDate, setEffectiveDate] = useState(initial?.effective_date || new Date().toISOString().slice(0, 10));
    const [comments, setComments] = useState(initial?.comments || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const options = FIELD_STATUS_OPTIONS[field] || [];

    const submit = async () => {
        if (!value) { setError('Choose a status.'); return; }
        if (!effectiveDate) { setError('Effective date is required.'); return; }
        setSaving(true); setError('');
        try {
            await onSubmit({ status_value: value, effective_date: effectiveDate, comments });
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="legal-form-section legal-timeline-form">
            {error && <div className="legal-form-error">{error}</div>}
            <div className="legal-form-grid">
                <div className="legal-field">
                    <label>Status <span className="legal-required">*</span></label>
                    <select value={value} onChange={e => setValue(e.target.value)}>
                        <option value="">Select status</option>
                        {options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                </div>
                <div className="legal-field">
                    <label>Effective Date <span className="legal-required">*</span></label>
                    <input type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} />
                </div>
                <div className="legal-field legal-form-full">
                    <label>Comments</label>
                    <textarea value={comments} onChange={e => setComments(e.target.value)} placeholder="Optional — why the status changed" />
                </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="legal-btn-save" disabled={saving} onClick={submit}>{saving ? 'Saving…' : 'Save Status Update'}</button>
                <button className="legal-btn-cancel" disabled={saving} onClick={onCancel}>Cancel</button>
            </div>
        </div>
    );
}

function FieldTimeline({
    field, entries, today, canManageData, editingId, addingNew,
    onStartEdit, onStartAdd, onCancelForm, onSubmitForm, onDelete, deleteBusyId,
}) {
    const withDuration = entries.map((entry, i) => {
        const next = entries[i + 1];
        const endDate = next ? next.effective_date : today;
        return { ...entry, days: daysBetweenIso(entry.effective_date, endDate), isCurrent: !next };
    });

    return (
        <div className="legal-form-section legal-timeline-section">
            <div className="legal-form-section-title">
                <span className="legal-form-section-icon">🕘</span>{STATUS_FIELD_LABELS[field]}
                {canManageData && addingNew !== field && (
                    <button className="legal-action-btn legal-timeline-add-btn" title={`Add a ${STATUS_FIELD_LABELS[field]} update`} onClick={() => onStartAdd(field)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                    </button>
                )}
            </div>
            {!withDuration.length && <p style={{ color: '#94a3b8', fontSize: 11.5, margin: '2px 0 8px' }}>No status recorded yet.</p>}
            {withDuration.map(entry => (
                editingId === entry.id ? (
                    <StatusUpdateForm
                        key={entry.id} field={field} initial={entry}
                        onCancel={onCancelForm}
                        onSubmit={payload => onSubmitForm({ ...payload, id: entry.id, status_field: field })}
                    />
                ) : (
                    <div key={entry.id} className="legal-timeline-row">
                        <span className={`legal-status-chip ${statusToneClass(entry.status_value)}`}>{entry.status_value || '—'}</span>
                        <span className="legal-timeline-date">{fmtDate(entry.effective_date)}</span>
                        <span className="legal-timeline-days">{entry.days}d{entry.isCurrent ? ' · current' : ''}</span>
                        <span className="legal-timeline-comment">{entry.comments || ''}</span>
                        {canManageData && (
                            <span className="legal-row-actions">
                                <button className="legal-action-btn" title="Edit this entry" onClick={() => onStartEdit(entry.id)}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                                </button>
                                <button
                                    className="legal-action-btn legal-action-danger" title="Delete this entry"
                                    disabled={deleteBusyId === entry.id} onClick={() => onDelete(entry)}
                                >
                                    {deleteBusyId === entry.id ? '…' : (
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                    )}
                                </button>
                            </span>
                        )}
                    </div>
                )
            ))}
            {addingNew === field && (
                <StatusUpdateForm field={field} onCancel={onCancelForm} onSubmit={payload => onSubmitForm({ ...payload, status_field: field })} />
            )}
        </div>
    );
}

// ── LegalTimelineModal — the popup itself, reusable from any screen that
// lists documents (Manage Legal Master, Aging, Insights). Shows every status
// field's effective-dated history with per-stage duration; masters can add a
// new status update (pencil icon) or correct an existing entry's date/value/
// comments (their own edit/delete icons) if it was entered by mistake. ─────
export function LegalTimelineModal({ recordId, entityName, canManageData = false, onClose, onChanged }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [addingField, setAddingField] = useState(null);
    const [deleteBusyId, setDeleteBusyId] = useState(null);

    const load = async () => {
        setLoading(true); setError('');
        try {
            const res = await fetch(`${API_URL}/api/legal/status-history/?record_id=${recordId}`, { credentials: 'include' });
            const body = await readApiJson(res, 'Could not load status history.');
            setData(body);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [recordId]);

    const closeForms = () => { setEditingId(null); setAddingField(null); };

    const submitForm = async (payload) => {
        const res = await fetch(`${API_URL}/api/legal/status-history/save/`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
            body: JSON.stringify({ ...payload, record_id: recordId }),
        });
        await readApiJson(res, 'Could not save the status update.');
        closeForms();
        await load();
        onChanged?.();
    };

    const deleteEntry = async (entry) => {
        if (!window.confirm(`Delete this ${STATUS_FIELD_LABELS[entry.status_field]} entry (${entry.status_value || 'blank'}, ${fmtDate(entry.effective_date)})?`)) return;
        setDeleteBusyId(entry.id); setError('');
        try {
            const res = await fetch(`${API_URL}/api/legal/status-history/save/`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                body: JSON.stringify({ delete_id: entry.id }),
            });
            await readApiJson(res, 'Could not delete the entry.');
            await load();
            onChanged?.();
        } catch (err) {
            setError(err.message);
        } finally {
            setDeleteBusyId(null);
        }
    };

    return (
        <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="legal-modal legal-modal-wide">
                <div className="legal-modal-hdr">
                    <div className="legal-modal-icon">🕘</div>
                    <div>
                        <div className="legal-modal-title">Status Timeline</div>
                        <div className="legal-modal-sub">{entityName || 'Document'} — effective-dated status history, per field</div>
                    </div>
                    <button className="legal-modal-close" onClick={onClose} style={{ marginLeft: 'auto' }}>✕</button>
                </div>
                <div className="legal-modal-body">
                    {error && <div className="legal-form-error">{error}</div>}
                    {loading ? <p style={{ color: '#65676b' }}>Loading…</p> : (
                        STATUS_FIELD_ORDER.map(field => (
                            <FieldTimeline
                                key={field} field={field}
                                entries={data?.history?.[field] || []}
                                today={data?.today}
                                canManageData={canManageData}
                                editingId={editingId} addingNew={addingField}
                                onStartEdit={id => { setAddingField(null); setEditingId(id); }}
                                onStartAdd={f => { setEditingId(null); setAddingField(f); }}
                                onCancelForm={closeForms}
                                onSubmitForm={submitForm}
                                onDelete={deleteEntry}
                                deleteBusyId={deleteBusyId}
                            />
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
